import {
    AccountingReportCycle,
    AccountingReportCycleOptionType,
    AccountingReportCycleTotal,
} from "@common/constants";
import { omitBy } from "@common/helpers/objects";
import { SchedulesReportView } from "@common/types/views/schedulesReportView";
import { AccountingReportFilters } from "@components/accounting/types";
import {
    localCalendarDayAsUTCMidnight,
    utcCalendarDayAsLocalMidnight,
} from "@components/accounting/reportViews/reportViewDates";
import {
    isSchedulesReportDatePresetId,
    SchedulesReportDatePresetId,
} from "./schedulesReportDatePresets";
import { SchedulesReportParams } from "./schedulesReportTypes";

export interface SchedulesReportPageState {
    params: SchedulesReportParams;
    datePreset: SchedulesReportDatePresetId | null;
    filters: Pick<
        AccountingReportFilters,
        | "startDate"
        | "endDate"
        | "cycle"
        | "classes"
        | "classFilterType"
        | "counterpartiesIds"
        | "withEmptyOrUnverifiedCounterparty"
        | "hasCounterparty"
        | "minAmount"
        | "maxAmount"
    >;
}

function toViewCycle(
    cycle: AccountingReportCycleOptionType | undefined,
): AccountingReportCycle | undefined {
    return cycle === undefined || cycle === AccountingReportCycleTotal.TOTAL
        ? undefined
        : cycle;
}

/**
 * Report state → persisted view filters. Dates are stored only for custom
 * ranges; entity and ledger stay page context so a view applies to any entity.
 */
export function buildSchedulesReportViewFilters({
    params,
    datePreset,
    filters,
}: SchedulesReportPageState): SchedulesReportView["filters"] {
    const viewFilters = {
        scheduleLayout: params.layout,
        scheduleRows: params.rows,
        revenueSubMode: params.revenueSubMode,
        scheduleMethod: params.method,
        datePreset: datePreset ?? undefined,
        start:
            datePreset || !filters.startDate
                ? undefined
                : localCalendarDayAsUTCMidnight(filters.startDate),
        end:
            datePreset || !filters.endDate
                ? undefined
                : localCalendarDayAsUTCMidnight(filters.endDate),
        cycle: toViewCycle(filters.cycle),
        classes: filters.classes,
        classFilterType: filters.classFilterType,
        counterpartiesIds: filters.counterpartiesIds,
        withEmptyOrUnverifiedCounterparty:
            filters.withEmptyOrUnverifiedCounterparty,
        hasCounterparty: filters.hasCounterparty,
        minAmount: filters.minAmount,
        maxAmount: filters.maxAmount,
        scheduleTemplateIds: params.templateIds,
        holdingAccountIds: params.holdingAccountIds,
        scheduleGroups: params.groups,
        termStartFrom: params.termStartFrom,
        termStartTo: params.termStartTo,
        termEndFrom: params.termEndFrom,
        termEndTo: params.termEndTo,
    };
    return omitBy(viewFilters, (value) => value === undefined);
}

/** Persisted view filters → report state to apply when a view is selected. */
export function applySchedulesReportViewFilters(
    viewFilters: SchedulesReportView["filters"],
): SchedulesReportPageState {
    const datePreset = isSchedulesReportDatePresetId(viewFilters.datePreset)
        ? viewFilters.datePreset
        : null;
    return {
        params: {
            layout: viewFilters.scheduleLayout,
            rows: viewFilters.scheduleRows,
            revenueSubMode: viewFilters.revenueSubMode,
            method: viewFilters.scheduleMethod,
            templateIds: viewFilters.scheduleTemplateIds,
            holdingAccountIds: viewFilters.holdingAccountIds,
            groups: viewFilters.scheduleGroups,
            termStartFrom: viewFilters.termStartFrom,
            termStartTo: viewFilters.termStartTo,
            termEndFrom: viewFilters.termEndFrom,
            termEndTo: viewFilters.termEndTo,
        },
        datePreset,
        filters: {
            startDate:
                datePreset || !viewFilters.start
                    ? undefined
                    : utcCalendarDayAsLocalMidnight(viewFilters.start),
            endDate:
                datePreset || !viewFilters.end
                    ? undefined
                    : utcCalendarDayAsLocalMidnight(viewFilters.end),
            cycle: viewFilters.cycle ?? AccountingReportCycleTotal.TOTAL,
            classes: viewFilters.classes,
            classFilterType: viewFilters.classFilterType,
            counterpartiesIds: viewFilters.counterpartiesIds,
            withEmptyOrUnverifiedCounterparty:
                viewFilters.withEmptyOrUnverifiedCounterparty,
            hasCounterparty: viewFilters.hasCounterparty,
            minAmount: viewFilters.minAmount,
            maxAmount: viewFilters.maxAmount,
        },
    };
}
